/**
 * Evidence Viewer - Browse and trace evidence
 */

import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Card } from '../UI/Card';
import { Button } from '../UI/Button';
import { Input } from '../UI/Input';
import { Badge } from '../UI/Badge';
import { 
  listEvidence,
  getEvidence,
  traceEvidence,
} from '../../api/evidence';
import { useWorkspace } from '../../hooks/useWorkspace';
import { 
  FileText, 
  Search, 
  ExternalLink,
  Hash,
  Book,
  GitBranch,
  ChevronLeft,
  ChevronRight,
} from 'lucide-react';

export function EvidenceViewer() {
  const { projectId } = useWorkspace();
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedEvidence, setSelectedEvidence] = useState<string | null>(null);
  const [page, setPage] = useState(0);
  const [pageSize] = useState(20);

  const { data: evidenceList, isLoading } = useQuery({
    queryKey: ['evidence', projectId],
    queryFn: () => listEvidence(projectId!, undefined, 1000),
    enabled: !!projectId,
  });

  const { data: traceData } = useQuery({
    queryKey: ['evidence-trace', selectedEvidence],
    queryFn: () => traceEvidence(selectedEvidence!),
    enabled: !!selectedEvidence,
  });

  const filteredEvidence = React.useMemo(() => {
    if (!evidenceList) return [];
    return evidenceList.filter(e => 
      e.content?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      e.id?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      e.type?.toLowerCase().includes(searchTerm.toLowerCase())
    );
  }, [evidenceList, searchTerm]);

  const paginatedEvidence = React.useMemo(() => {
    const start = page * pageSize;
    return filteredEvidence.slice(start, start + pageSize);
  }, [filteredEvidence, page, pageSize]);

  const totalPages = Math.ceil(filteredEvidence.length / pageSize);

  if (!projectId) {
    return (
      <Card>
        <p className="text-center py-12 text-gray-500">Select a project to view evidence</p>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Book className="h-8 w-8 text-blue-600" />
          <div>
            <h1 className="text-3xl font-bold text-gray-900 dark:text-gray-100">Evidence Registry</h1>
            <p className="text-gray-600 mt-1">
              {filteredEvidence.length} evidence entries
            </p>
          </div>
        </div>
      </div>

      {/* Search */}
      <Card>
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
          <Input
            placeholder="Search evidence by content, ID, or type..."
            value={searchTerm}
            onChange={(e) => {
              setSearchTerm(e.target.value);
              setPage(0);
            }}
            className="pl-10"
          />
        </div>
      </Card>

      {/* Evidence List */}
      <div className="space-y-3">
        {paginatedEvidence.map((evidence) => (
          <Card key={evidence.id}>
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-2">
                  <Badge variant="default">{evidence.type}</Badge>
                  {evidence.page_number && (
                    <span className="text-xs text-gray-500">Page {evidence.page_number}</span>
                  )}
                  {evidence.table_id && (
                    <span className="text-xs text-gray-500">Table {evidence.table_id}</span>
                  )}
                </div>
                <div className="text-sm text-gray-700 mb-2 line-clamp-3">
                  {evidence.content}
                </div>
                <div className="flex items-center gap-4 text-xs text-gray-500">
                  <span className="flex items-center gap-1">
                    <Hash className="h-3 w-3" />
                    {evidence.content_hash?.substring(0, 16)}...
                  </span>
                  <span className="font-mono">{evidence.id.substring(0, 8)}...</span>
                </div>
              </div>
              <Button
                size="sm"
                variant="outline"
                onClick={() => setSelectedEvidence(evidence.id)}
              >
                <GitBranch className="h-4 w-4" />
                Trace
              </Button>
            </div>
          </Card>
        ))}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <Card>
          <div className="flex items-center justify-between">
            <div className="text-sm text-gray-600">
              Page {page + 1} of {totalPages}
            </div>
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPage(p => Math.max(0, p - 1))}
                disabled={page === 0}
              >
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPage(p => Math.min(totalPages - 1, p + 1))}
                disabled={page === totalPages - 1}
              >
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </Card>
      )}

      {/* Trace Modal */}
      {selectedEvidence && traceData && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={() => setSelectedEvidence(null)}>
          <div className="max-w-4xl w-full mx-4" onClick={(e) => e.stopPropagation()}>
            <Card>
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <h3 className="text-lg font-semibold">Evidence Trace</h3>
                  <button
                    onClick={() => setSelectedEvidence(null)}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    ✕
                  </button>
                </div>

                <div className="space-y-4">
                  {/* Evidence */}
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                    <h4 className="font-medium text-blue-900 mb-2">Evidence</h4>
                    <div className="space-y-2 text-sm">
                      <div><span className="text-blue-700 font-medium">ID:</span> {traceData.evidence.id}</div>
                      <div><span className="text-blue-700 font-medium">Type:</span> {traceData.evidence.type}</div>
                      <div><span className="text-blue-700 font-medium">Page:</span> {traceData.evidence.page_number || 'N/A'}</div>
                      <div><span className="text-blue-700 font-medium">Hash:</span> <code className="text-xs">{traceData.evidence.content_hash}</code></div>
                    </div>
                  </div>

                  {/* Chunk */}
                  <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                    <h4 className="font-medium text-green-900 mb-2">Chunk</h4>
                    <div className="space-y-2 text-sm">
                      <div><span className="text-green-700 font-medium">ID:</span> {traceData.chunk.id}</div>
                      <div><span className="text-green-700 font-medium">Strategy:</span> {traceData.chunk.strategy}</div>
                      <div><span className="text-green-700 font-medium">Position:</span> {traceData.chunk.position}</div>
                      <div className="bg-white p-2 rounded text-gray-700 max-h-32 overflow-y-auto">
                        {traceData.chunk.content.substring(0, 500)}...
                      </div>
                    </div>
                  </div>

                  {/* Artifact */}
                  <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
                    <h4 className="font-medium text-purple-900 mb-2">Source Artifact</h4>
                    <div className="space-y-2 text-sm">
                      <div><span className="text-purple-700 font-medium">Filename:</span> {traceData.artifact.filename}</div>
                      <div><span className="text-purple-700 font-medium">Type:</span> {traceData.artifact.artifact_type}</div>
                      <div><span className="text-purple-700 font-medium">Size:</span> {(traceData.artifact.file_size_bytes / 1024).toFixed(2)} KB</div>
                      <div><span className="text-purple-700 font-medium">SHA256:</span> <code className="text-xs">{traceData.artifact.sha256}</code></div>
                    </div>
                    <Button className="mt-3 w-full" onClick={() => {
                      window.open(`/artifacts/${traceData.artifact.id}/download`, '_blank');
                    }}>
                      <ExternalLink className="h-4 w-4" />
                      Download Source Document
                    </Button>
                  </div>
                </div>
              </div>
            </Card>
          </div>
        </div>
      )}
    </div>
  );
}

